import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen, ChevronDown, X } from 'lucide-react';
import { MODULE_GROUPS, findModuleByPath } from '../data/navigation';
import { getAuthenticatedUser, getCurrentUser, isSuperAdminRole } from '../data/settingsSelectors';

import serojaLogo from '../assets/images/seroja-logo.png';
import serojaAppIcon from '../assets/images/seroja-appicon.png';

function SidebarNavItem({ item, collapsed, childActive, onNavigate }) {
  const location = useLocation();
  const Icon = item.icon;
  const hasChildren = item.children && item.children.length > 0;
  const [userOpen, setUserOpen] = useState(false);
  const open = hasChildren && (userOpen || childActive);
  const submenuRef = useRef(null);
  const [hasActiveChild, setHasActiveChild] = useState(false);

  useLayoutEffect(() => {
    const submenu = submenuRef.current;
    if (!childActive || !submenu) {
      setHasActiveChild(false);
      return;
    }
    const activeLink = submenu.querySelector('.app-sidebar__submenu-link--active');
    if (!activeLink) {
      setHasActiveChild(false);
      return;
    }
    const center = activeLink.offsetTop + activeLink.offsetHeight / 2;
    submenu.style.setProperty('--active-center', `${center}px`);
    setHasActiveChild(true);
  }, [childActive, location.pathname]);

  if (!hasChildren) {
    return (
      <NavLink
        to={item.path}
        end={item.path === '/dashboard'}
        onClick={onNavigate}
        className={({ isActive }) =>
          ['app-sidebar__item', isActive ? 'app-sidebar__item--active' : ''].join(' ')
        }
        title={collapsed ? item.label : undefined}
      >
        <span className="app-sidebar__item-icon">
          <Icon size={18} strokeWidth={2} />
        </span>
        {!collapsed && (
          <span className="app-sidebar__item-label">{item.label}</span>
        )}
      </NavLink>
    );
  }

  return (
    <div
      className={`app-sidebar__item-group${
        childActive && hasActiveChild ? ' app-sidebar__item-group--child-active' : ''
      }`}
    >
      <NavLink
        to={item.path}
        className={({ isActive }) =>
          ['app-sidebar__item', isActive ? 'app-sidebar__item--active' : ''].join(' ')
        }
        title={collapsed ? item.label : undefined}
        onClick={() => {
          setUserOpen((o) => !o);
          if (onNavigate) onNavigate();
        }}
      >
        <span className="app-sidebar__item-icon">
          <Icon size={18} strokeWidth={2} />
        </span>
        {!collapsed && (
          <span className="app-sidebar__item-label">{item.label}</span>
        )}
        {!collapsed && (
          <ChevronDown
            size={16}
            className={`app-sidebar__item-chevron${open ? ' app-sidebar__item-chevron--open' : ''}`}
          />
        )}
      </NavLink>
      {!collapsed && open && (
        <div ref={submenuRef} className="app-sidebar__submenu">
          {item.children.map((child) => {
            const activeModule = findModuleByPath(location.pathname);
            const isChildActive = activeModule?.id === child.id;
            return (
              <NavLink
                key={child.id}
                to={child.path}
                onClick={onNavigate}
                className={[
                  'app-sidebar__submenu-link',
                  isChildActive ? 'app-sidebar__submenu-link--active' : '',
                ].join(' ')}
              >
                {child.label}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AppSidebar({ collapsed, onToggle, onMobileClose = null }) {
  const location = useLocation();
  const currentRoleType = useMemo(() => getCurrentUser().roleType, []);
  // Real API session role (e.g. 'superadmin'); the seed user above only knows legacy role types.
  const authRoleType = useMemo(() => getAuthenticatedUser()?.roleType, []);

  const canSee = (child) => {
    if (!child.requiresRole) return true;
    // 'superadmin' is checked against the real API role only (never the seed fallback user).
    if (child.requiresRole === 'superadmin') return isSuperAdminRole(authRoleType);
    return child.requiresRole === currentRoleType || child.requiresRole === authRoleType;
  };

  const visibleChildren = (item) =>
    item.children?.filter(canSee) ?? [];

  return (
    <aside className={`app-sidebar${collapsed ? ' app-sidebar--collapsed' : ''}`}>
      {/* ── Header: brand + collapse toggle (desktop) / close (mobile) ── */}
      <div className="app-sidebar__header">
        <NavLink to="/dashboard" className="app-sidebar__brand" title="Seroja — Dashboard">
          {collapsed ? (
            <img className="app-sidebar__brand-mark" src={serojaAppIcon} alt="Seroja" />
          ) : (
            <img className="app-sidebar__brand-logo" src={serojaLogo} alt="Seroja" />
          )}
        </NavLink>

        {onMobileClose && (
          <button
            type="button"
            className="app-sidebar__mobile-close"
            onClick={onMobileClose}
            aria-label="Tutup menu"
            title="Tutup menu"
          >
            <X size={20} />
          </button>
        )}

        <button
          type="button"
          className="app-sidebar__collapse"
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
      </div>

      {/* ── Grouped module menu ─────────────────────────────────────── */}
      <nav className="app-sidebar__nav">
        {MODULE_GROUPS.map((group) => (
          <div key={group.id} className="app-sidebar__group">
            {group.label && !collapsed && (
              <div className="app-sidebar__group-label">{group.label}</div>
            )}
            {group.label && collapsed && (
              <div className="app-sidebar__group-divider" aria-hidden="true" />
            )}
            {group.items.map((item) => {
              const children = visibleChildren(item);
              if (item.children && children.length === 0) return null;
              const activeModule = findModuleByPath(location.pathname);
              const childActive =
                children.some((child) => child.id === activeModule?.id) ?? false;
              const displayItem = item.children ? { ...item, children } : item;
              return (
                <SidebarNavItem
                  key={item.id}
                  item={displayItem}
                  collapsed={collapsed}
                  childActive={childActive}
                  onNavigate={onMobileClose}
                />
              );
            })}
          </div>
        ))}
      </nav>

      {/* ── Footer note (v4-style) ──────────────────────────────────── */}
      <div className="app-sidebar__footer">
        {!collapsed ? (
          <>
            <div className="app-sidebar__footer-title">Bursaku POS + CRM</div>
            <div className="app-sidebar__footer-version">Build #5 • Beta v5</div>
          </>
        ) : (
          <div className="app-sidebar__footer-mark" title="Bursaku POS + CRM">v5</div>
        )}
      </div>
    </aside>
  );
}
